import { el, KEYED_LIST, VNode } from "@dhx/ts-common/dom";
import { getScrollbarWidth } from "@dhx/ts-common/html";
import { ILayoutState, IRendererConfig } from "./../types";
import { getCells, getHandlers, getSpans } from "./Cells";
import { getFixedRows, getRows } from "./FixedRows";
import { getTotalWidth } from "../helpers/main";

export function getFixedColsHeader(renderConfig: IRendererConfig, layout: ILayoutState) {
	if (typeof renderConfig.leftSplit !== "number") {
		return;
	}
	let splitHidden = 0;
	for (let index = 0; index < renderConfig.leftSplit; index++) {
		if (renderConfig.columns[index].hidden) splitHidden++;
	}
	if (splitHidden === renderConfig.leftSplit) {
		return;
	}

	const columns = renderConfig.columns.slice(0, renderConfig.leftSplit - splitHidden);
	let width = 0;
	for (const col of columns) {
		width += col.$width;
	}

	const getRowAriaAttrs = count => ({
		role: "rowgroup",
		"aria-rowcount": count,
	});

	const frozenHeaderCols =
		renderConfig.leftSplit >= 0 &&
		getFixedRows(
			{
				...renderConfig,
				currentColumns: columns,
				$positions: {
					...renderConfig.$positions,
					xStart: 0,
					xEnd: renderConfig.leftSplit,
				},
				scroll: { top: 0, left: 0 },
				columns,
			},
			{ ...layout, name: "header", position: "top", shifts: { x: 0, y: 0 } }
		);

	const headerRowsConfig = { ...layout, name: "header", position: "top" };
	return (
		frozenHeaderCols &&
		el(
			`.dhx_${headerRowsConfig.name}-fixed-cols`,
			{
				style: {
					position: "absolute",
					top: 0,
					left: 0,
					maxWidth: width,
					overflow: "hidden",
				},
				...getRowAriaAttrs(frozenHeaderCols.length),
			},
			frozenHeaderCols.body
		)
	);
}

export function getFixedCols(renderConfig: IRendererConfig, layout: ILayoutState) {
	if (typeof renderConfig.leftSplit !== "number") {
		return;
	}
	let splitHidden = 0;
	for (let index = 0; index < renderConfig.leftSplit; index++) {
		if (renderConfig.columns[index].hidden) splitHidden++;
	}
	if (splitHidden === renderConfig.leftSplit) {
		return;
	}

	const scrollBarWidth = renderConfig.$totalWidth <= layout.wrapper.width ? 0 : getScrollbarWidth();
	const fixedContentHeight =
		renderConfig.$totalHeight + renderConfig.headerHeight + renderConfig.footerHeight;

	const fixedColsHeight =
		fixedContentHeight > layout.gridBodyHeight
			? fixedContentHeight - scrollBarWidth
			: fixedContentHeight < layout.gridBodyHeight - scrollBarWidth
			? fixedContentHeight
			: layout.gridBodyHeight;

	const columns = renderConfig.columns.slice(0, renderConfig.leftSplit - splitHidden);
	renderConfig.fixedColumnsWidth = getTotalWidth(columns);

	const fixedCols = getCells({
		...renderConfig,
		columns,
		$positions: {
			...renderConfig.$positions,
			xStart: 0,
			xEnd: renderConfig.leftSplit,
		},
	});

	const isSticky = layout.sticky;

	const footerRowsConfig = { ...layout, name: "footer", position: "bottom" };

	const frozenFooterCols =
		renderConfig.leftSplit >= 0 &&
		getRows(
			{
				...renderConfig,
				currentColumns: columns,
				$positions: {
					...renderConfig.$positions,
					xStart: 0,
					xEnd: renderConfig.leftSplit,
				},
			},
			{ ...layout, name: "footer", position: "bottom" }
		);
	let footerHeight = 0;
	if (frozenFooterCols) {
		frozenFooterCols.forEach(node => (footerHeight += (node as VNode).attrs.style.height));
	}

	const getRowAriaAttrs = count => ({
		role: "rowgroup",
		"aria-rowcount": count,
	});

	const frozenFooter = isSticky
		? frozenFooterCols &&
		  el(
				`.dhx_${footerRowsConfig.name}-fixed-cols`,
				{
					style: {
						position: "absolute",
						top: fixedColsHeight < layout.gridBodyHeight ? fixedColsHeight - footerHeight : null,
						left: 0,
						bottom:
							fixedColsHeight >= layout.gridBodyHeight
								? `${0 + (isSticky ? scrollBarWidth : 0)}px`
								: null,
					},
					...getRowAriaAttrs(frozenFooterCols.length),
				},
				frozenFooterCols
		  )
		: null;

	const pos = renderConfig.$positions;
	const spans = getSpans(renderConfig, true);
	const getFixedColAriaAttrs = () => ({
		role: "presentation",
		"aria-label": "Fixed column",
	});

	return [
		el(
			".dhx_grid-fixed-cols-wrap",
			{
				style: {
					height:
						fixedColsHeight >= layout.gridBodyHeight
							? (isSticky
									? layout.gridBodyHeight
									: layout.gridBodyHeight + renderConfig.headerHeight) - scrollBarWidth
							: fixedColsHeight,
					paddingTop: renderConfig.headerHeight,
					overflow: "hidden",
					width: renderConfig.fixedColumnsWidth,
				},
				...getFixedColAriaAttrs(),
			},
			[
				el(
					".dhx_grid-fixed-cols",
					{
						style: {
							top: -renderConfig.scroll.top + renderConfig.headerHeight - 1 + "px",
							paddingTop: layout.shifts.y,
							height: renderConfig.$totalHeight,
							position: "absolute",
						},
						_flags: KEYED_LIST,
						...getHandlers(pos.yStart, pos.xStart, renderConfig),
						...getRowAriaAttrs(renderConfig.data.length),
					},
					[spans && el(".dhx_span-spans", { role: "presentation" }, [spans]), ...fixedCols]
				),
				el(".dhx_frozen-cols-border", { role: "presentation" }),
			]
		),
		renderConfig.$footer ? frozenFooter : null,
	];
}
